import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@core-ui/Components/ui/dialog';
import { Button } from '@core-ui/Components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import useToastMessage from '@lib/logger/toast.ts';

interface Props {
  open: boolean;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  cancelKey?: string;
  disableCloseOnLoading?: boolean;
}

export default function ConfirmEndSessionDialog ({
  open,
  loading = false,
  onCancel,
  onConfirm,
  cancelKey = 'adminConsole.common.cancel',
  disableCloseOnLoading = true,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const { showToast } = useToastMessage();

  const handleOpenChange = (isOpen: boolean): void => {
    if (disableCloseOnLoading && loading) {
      return;
    }
    if (!isOpen) {
      onCancel();
    }
  };

  const handleEndConfirm = (): void => {
    onConfirm();
    showToast('info', `${t('wizard.dashboard.pausedToast')}`);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-xl rounded-2xl shadow-lg border border-border bg-card text-foreground p-0"
        onEscapeKeyDown={(e) => {
          if (disableCloseOnLoading && loading) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (disableCloseOnLoading && loading) {
            e.preventDefault();
          }
        }}
      >
        <div className="px-6 pt-6">
          <DialogHeader className="mb-2">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-destructive/30 bg-destructive/10 text-destructive">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <DialogTitle className="text-lg font-semibold">
                {t('wizard.dashboard.endSession')}
              </DialogTitle>
            </div>
          </DialogHeader>

          <DialogDescription asChild>
            <div className="text-sm leading-relaxed text-muted-foreground">
              <Trans
                i18nKey="wizard.dashboard.endCallSession"
                defaults="Are you sure you want to end your session?"
                components={{ strong: <span className="font-semibold text-foreground" /> }}
              />
            </div>
          </DialogDescription>
        </div>

        <div className="px-6 pb-6 mt-6 flex justify-end gap-2">
          <Button
            data-qa-id="cancel-delete-org-form-button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="border border-border text-foreground hover:bg-muted"
            type="button"
            autoFocus
          >
            {t(cancelKey, 'Cancel')}
          </Button>
          <Button
            data-qa-id="delete-org-form-button"
            variant="destructive"
            onClick={handleEndConfirm}
            disabled={loading}
            className="text-primary-foreground"
            type="button"
          >
            {loading
              ? t('adminConsole.organizations.deleting', 'Deleting…')
              : t('wizard.dashboard.endSession')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
